<template>
  <div :class="$style.root">
    <ProductNoveltyEndText :product="products || []" />
    <AtomsConsumerButton v-if="props.isWrappingRecommend" variant="primary" block @click.prevent="wrappingAddToCart">
      ラッピング設定に進む
    </AtomsConsumerButton>
    <AtomsConsumerButton v-else variant="primary" block @click.prevent="addToCart">
      すべての商品をカートに追加
    </AtomsConsumerButton>
    <OrderPreOrderAttention v-model:visible="isPreOrderModalVisible" @confirm="beforeVisibleCheckout" />
    <OrderDuvetCollectionModal
      v-model:visible="isDuvetCollectionModalVisible"
      @cancel="isDuvetCollectionModalVisible = false"
      @done="duvetCollectionModalDone"
    />
  </div>
</template>

<script setup lang="ts">
import { isEmbroideryProductsInCart } from "@tential/ec-gql-schema/utils/embroidery"
import { SkuFragmentFragment } from "~/types/type.generated"
import { PagePartsProductType } from "~/types/product"
import { useDuvetCollection } from "~/composables/useDuvetCollection"
import { useStore } from "~/stores"
import { useToast } from "~/state"

interface Props {
  products: PagePartsProductType[] | null | undefined
  selectedSkus: SkuFragmentFragment[] | []
  selectedSize: string[]
  isWrappingRecommend?: boolean
}
const props = defineProps<Props>()

const emit = defineEmits<{
  (e: "setSelectedSizeError"): void
  (e: "beforeVisibleCheckout"): void
  (e: "wrappingAddToCart"): void
}>()

const isPreOrderModalVisible = ref<boolean>(false)
const isDuvetCollectionModalVisible = ref<boolean>(false)

const store = useStore()
const toast = useToast()
const { showDuvetCollection, getDuvetCollectionType } = useDuvetCollection()

const isEmbroidery = computed(() => isEmbroideryProductsInCart(store.cartList))

const isIncludeReservation = computed(() => {
  return props.selectedSkus.some((sku) => sku.is_reservation)
})

const isShowDuvetCollectionModal = computed(() => {
  return (
    showDuvetCollection() &&
    props.products?.some((product) => product.is_duvet_collection) &&
    getDuvetCollectionType() === "none"
  )
})

const selectSizeValidation = (): boolean => {
  // マルチカート内の全ての商品のサイズが選択されているかどうかを判定
  const productLength = props.products?.length ?? 0
  return props.selectedSize.length >= productLength && props.selectedSize.every((size) => size !== "")
}

const addToCart = () => {
  if (isEmbroidery.value) {
    toast?.showErrorToasted("刺繍商品と通常商品は同時にカートに追加することができません")
    return
  }

  if (!selectSizeValidation()) {
    emit("setSelectedSizeError")
    toast?.showErrorToasted("サイズを選択してください")
    return
  }

  if (isIncludeReservation.value) {
    isPreOrderModalVisible.value = true
  } else if (isShowDuvetCollectionModal.value && !props.isWrappingRecommend) {
    isDuvetCollectionModalVisible.value = true
  } else {
    emit("beforeVisibleCheckout")
  }
}

/**
 * 予約注文モーダル後の処理
 */
const beforeVisibleCheckout = () => {
  if (isShowDuvetCollectionModal.value && !props.isWrappingRecommend) {
    isDuvetCollectionModalVisible.value = true
  } else if (props.isWrappingRecommend) {
    emit("wrappingAddToCart")
  } else {
    emit("beforeVisibleCheckout")
  }
}

/**
 * 羽毛布団改修キャンペーンモーダル後の処理
 */
const duvetCollectionModalDone = () => {
  isDuvetCollectionModalVisible.value = false
  if (props.isWrappingRecommend) {
    emit("wrappingAddToCart")
  } else {
    emit("beforeVisibleCheckout")
  }
}

/**
 * ラッピング誘導モードの場合のカートに追加処理
 */
const wrappingAddToCart = () => {
  if (isIncludeReservation.value) {
    isPreOrderModalVisible.value = true
  } else {
    emit("wrappingAddToCart")
  }
}
</script>

<style scoped module lang="scss">
.root {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  p {
    font-size: $font-size-10;
    @include md {
      font-size: $font-size-12;
    }
  }

  button {
    width: 100%;
    display: flex;
    justify-content: center;
  }
}
</style>
